import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { CUSTOMER_COLUMNS } from '../../..//constants/table';
import { QUERY_KEYS } from '../../../constants/queryKey';
import { getAllCustomer } from '../../../utils/apiServices/customerApi';
import { usePagination } from '../../../hooks';
import { Table } from '../../shared/Table';
import { CUSTOMER_LIST, ROLES } from '../../../constants/strings';
import { updateUserVisitPageAPI } from '../../../utils/apiServices/userReportAPI';
import { LOCAL_STORAGE } from '../../../constants';
import { getLocalStorageItem } from '../../../utils/common/localStorage';
import { setSelectedAccounts, setSelectedAccountName } from '../../../redux';
import { ROUTES } from '../../../constants/routes';

const AccountsListing = () => {
    const pagination = usePagination(0, 0);
    const userRole = useSelector((s) => s.user.role);
    const [search, setSearch] = useState('');
    const [serverSort, setServerSort] = useState({ column: 'updated_at', sort: 'desc' });
    const [statusFilter, setStatusFilter] = useState('all');
    const [regionFilter, setRegionFilter] = useState('');
    const [industryFilter, setIndustryFilter] = useState('');
    const [response, setResponse] = useState({ data: [], count: 0 });

    const userId = getLocalStorageItem(LOCAL_STORAGE.USER_ID);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const visitRef = useRef(null);

    const accountsQuery = useQuery({
        queryKey: [
            QUERY_KEYS.CUSTOMERS,
            pagination.offset,
            pagination.limit,
            search,
            serverSort.column,
            serverSort.sort,
            statusFilter,
            regionFilter,
            industryFilter,
        ],
        queryFn: async () => {
            const filter = {};
            if (statusFilter === 'active') filter.status = true;
            if (statusFilter === 'archived') filter.status = false;
            if (regionFilter && regionFilter.trim()) filter.region = regionFilter.trim();
            if (industryFilter && industryFilter.trim()) filter.industry = industryFilter.trim();
            const hasFilter = Object.keys(filter).length > 0;
            return await getAllCustomer({
                offset: pagination.offset,
                limit: pagination.limit,
                search,
                orderBy: serverSort,
                filter: hasFilter ? filter : undefined,
            });
        },
        select: (data) => ({ data: data.data, count: data.count }),
        placeholderData: { data: [], count: 0 },
    });

    useEffect(() => {
        if (accountsQuery.data && accountsQuery.isFetched) {
            setResponse(accountsQuery.data);
        }
    }, [accountsQuery.data, accountsQuery.isFetched]);

    useEffect(() => {
        const path = window.location.pathname;
        const send = async (data = {}) => {
            try {
                await updateUserVisitPageAPI({ accountId: undefined, userId, path, data });
            } catch (e) {
                console.warn('visit logging failed', e);
            }
        };
        visitRef.current = send;
        visitRef.current({ entry_time: moment.utc().toISOString() });
        return () => {
            visitRef.current && visitRef.current({ exit_time: moment.utc().toISOString() });
            visitRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const baseColumns = [ROLES.ADMIN, ROLES.SUPER_ADMIN].includes(userRole)
        ? CUSTOMER_COLUMNS
        : CUSTOMER_COLUMNS.filter((c) => c.name !== 'Created By');
    const presetKey = `accounts_columns_preset_${userRole || 'unknown'}`;
    const defaultPreset = baseColumns.map((c) => c.name);
    const savedPresetRaw = typeof window !== 'undefined' ? window.localStorage.getItem(presetKey) : null;
    let presetNames;
    try {
        presetNames = savedPresetRaw ? JSON.parse(savedPresetRaw) : defaultPreset;
    } catch {
        presetNames = defaultPreset;
    }
    useEffect(() => {
        try {
            if (!savedPresetRaw) {
                window.localStorage.setItem(presetKey, JSON.stringify(defaultPreset));
            }
        } catch (e) {
            console.warn('preset save failed', e);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userRole]);
    const finalColumns = presetNames
        .map((name) => baseColumns.find((c) => c.name === name))
        .filter(Boolean);

    const columnsWithAction = useMemo(() => {
        return [
            ...finalColumns,
            {
                name: 'Action',
                classNameTH: 'option-td',
                classNameTD: 'option-td',
                dataField: ['id', 'name'],
                customFormatter: ({ id }) => (
                    <button
                        type='button'
                        className='btn btn-outline-primary btn-sm'
                        onClick={(e) => {
                            e.stopPropagation();
                            dispatch(setSelectedAccounts(id));
                            dispatch(setSelectedAccountName(id));
                            navigate(ROUTES.CUSTOMER.DASHBOARD);
                        }}
                    >
                        View
                    </button>
                ),
            },
        ];
    }, [finalColumns, dispatch, navigate]);

    return (
        <div className='d-flex flex-column gap-3'>
            <div className='d-flex flex-wrap gap-2 align-items-center justify-content-between'>
                <h4 className='mb-0'>{CUSTOMER_LIST.PAGE_HEADING}</h4>
                <div className='d-flex gap-2'>
                    <input
                        type='text'
                        className='form-control'
                        placeholder={CUSTOMER_LIST.SEARCH_BAR_PLACEHOLDER}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <select
                        className='form-select'
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value='all'>All Status</option>
                        <option value='active'>Active</option>
                        <option value='archived'>Archived</option>
                    </select>
                    <input
                        type='text'
                        className='form-control'
                        placeholder='Region'
                        value={regionFilter}
                        onChange={(e) => setRegionFilter(e.target.value)}
                    />
                    <input
                        type='text'
                        className='form-control'
                        placeholder='Industry'
                        value={industryFilter}
                        onChange={(e) => setIndustryFilter(e.target.value)}
                    />
                </div>
            </div>
            <Table
                columns={columnsWithAction}
                data={response.data}
                pagination={pagination}
                pageName={CUSTOMER_LIST.PAGE_NAME}
                search={search}
                debouncedApiCall={() => {}}
                stickyColumns={['Client Name']}
                isLoading={accountsQuery.isFetching}
                setServerSort={setServerSort}
                columnsForServerSort={['name', 'email', 'created_at', 'csm_name', 'status']}
            />
        </div>
    );
};

export default AccountsListing;


